// Fallback content for the /temples page. The live content comes from the
// admin-managed "temples" CMS sections (see cms.js); anything the admin hasn't
// filled in falls back to the values here, so the page is never empty.

import heroPhoto from "../../assets/Images/Temples/hero-photo.png";
import statTemple from "../../assets/Images/Temples/stat-temple.svg";
import statHeart from "../../assets/Images/Temples/stat-heart.svg";
import statChisel from "../../assets/Images/Temples/stat-chisel.svg";
import statPin from "../../assets/Images/Temples/stat-pin.svg";
import whyRoots from "../../assets/Images/Temples/why-roots.svg";
import whyVillage from "../../assets/Images/Temples/why-village.svg";
import whyGeneration from "../../assets/Images/Temples/why-generation.svg";
import stepVerify from "../../assets/Images/Temples/step-verify.svg";
import stepContribute from "../../assets/Images/Temples/step-contribute.svg";
import stepMilestones from "../../assets/Images/Temples/step-milestones.svg";
import stepTemple from "../../assets/Images/Temples/step-temple.svg";
import storyBefore from "../../assets/Images/Temples/story-before.png";
import storyAfter from "../../assets/Images/Temples/story-after.png";

export const templesData = {
  hero: {
    tagline: "॥ देवालय जीर्णोद्धार ॥",
    titleLine1: "Rebuild The Temples",
    titleLine2: "That Built Our Faith",
    description:
      "Hundreds of centuries-old temples across India are crumbling. Fund verified restoration projects and bring prayer, festivals and village life back to them.",
    primaryCta: { label: "Donate Now", link: "#featured-temples" },
    secondaryCta: { label: "Explore Temples", link: "#featured-temples" },
    trustPoints: ["Site-verified temples", "Milestone-based funds", "Photo updates"],
    image: heroPhoto,
    badge: { value: "50+", label: "Temples reopened" },
  },

  stats: [
    { icon: statTemple, value: "50+", label: "Temples Restored" },
    { icon: statHeart, value: "18,000+", label: "Donors Across India" },
    { icon: statChisel, value: "300+", label: "Artisans Employed" },
    { icon: statPin, value: "15+", label: "States Covered" },
  ],

  whyTemples: {
    tag: "Why temples matter",
    titleStart: "Where Our Faith",
    titleHighlight: "And Memories Live",
    description:
      "For millions of us, the village temple is where our grandparents prayed, where we were blessed as children, and where every festival began. When it falls silent, a part of all of us goes quiet with it.",
    cards: [
      {
        icon: whyRoots,
        hindiLabel: "परंपरा",
        title: "Our Roots, Our Identity",
        description:
          "Every family has a temple it returns to — for a mundan, a wedding or a quiet prayer. Restoring it keeps our story connected to where it began.",
      },
      {
        icon: whyVillage,
        hindiLabel: "समुदाय",
        title: "The Heart Of The Village",
        description:
          "Aarti at dusk, bhajans on festival nights, a mela every year — the temple is where an entire village comes together.",
      },
      {
        icon: whyGeneration,
        hindiLabel: "विरासत",
        title: "For The Next Generation",
        description:
          "When we rebuild a temple, we pass on the same faith, traditions and sense of belonging that our elders once gave us.",
      },
    ],
    quote:
      "“A temple is not built of stone alone — it is built of the faith of everyone who has ever prayed in it.”",
  },

  featuredTemples: {
    tag: "Temples waiting for you",
    title: "Help Restore These Sacred Spaces",
    description:
      "Every project is site-verified by our team. Choose a temple and follow its restoration, step by step.",
  },

  howItWorks: {
    tag: "How it works",
    title: "How Temple Crowdfunding Works",
    description:
      "Every contribution goes into a verified, milestone-based project — so you always know exactly where your money goes.",
    steps: [
      {
        icon: stepVerify,
        title: "We Verify The Temple",
        description:
          "Our team visits the site with heritage experts and prepares a detailed restoration plan.",
      },
      {
        icon: stepContribute,
        title: "You Contribute",
        description: "Browse temple projects, see the work plan and donate any amount securely.",
      },
      {
        icon: stepMilestones,
        title: "Work Begins In Milestones",
        description:
          "Funds are released to trusted artisans in stages, only after each milestone is checked.",
      },
      {
        icon: stepTemple,
        title: "The Temple Lives Again",
        description:
          "You receive photo updates at every stage and a final report when it reopens.",
      },
    ],
  },

  restorationStory: {
    tag: "Restoration story",
    title: "From Ruins To Reverence",
    // Each story is one slide; visitors move between them with arrows / swipe.
    stories: [
      {
        description:
          "Closed for over a decade, Mata Rani Mandir in Pali is open again — thanks to donors like you.",
        beforeImage: storyBefore,
        afterImage: storyAfter,
        storyLabel: "The story",
        storyTitle: "A 300-Year-Old Temple, Silent For Eleven Years",
        storyParagraphs: [
          "After years of heavy monsoons, the roof of Mata Rani Mandir began to give way and the temple was locked for safety. Festivals moved to the roadside, and the village slowly lost its gathering place.",
          "Once the project went live, 655 donors came together. Local masons and stone carvers rebuilt the roof, strengthened the walls and restored the original carvings — and seven months later, the bells rang again.",
        ],
        quote: "“Today the whole village gathers here again for the evening aarti.”",
        quoteAuthor: "— Head Priest, Mata Rani Mandir, Pali",
        stats: [
          { value: "₹12L", label: "raised" },
          { value: "655", label: "donors" },
          { value: "7", label: "months" },
        ],
      },
    ],
  },

  monthlySevak: {
    tagline: "॥ नित्य सेवा ॥",
    titleLine1: "Become A Monthly",
    titleLine2: "Temple Sevak",
    description:
      "A small gift every month keeps restoration going all year — and keeps temples alive long after they reopen.",
    cta: { label: "Start Monthly Giving", link: "/monthly-donation" },
  },

  // "Start Monthly Giving" modal. Admin → Temples → Monthly Donation Box.
  donationBox: {
    tagline: "॥ दान ॥",
    title: "Offer Your Seva",
    subtitle: "Every rupee goes to this temple's restoration",
    donationTypes: "monthly", // monthly / once / both (both = Once / Monthly toggle)
    defaultMode: "monthly",
    contributionLabel: "My contribution goes to:",
    buttonLabel: "Continue Donation",
    monthlyLabel: "Monthly",
    defaultAmount: 2100,
    amounts: [
      { amount: 501, label: "" },
      { amount: 1100, label: "" },
      { amount: 2100, label: "Most Donated" },
      { amount: 5100, label: "" },
      { amount: 11000, label: "" },
    ],
  },

  nominate: {
    tag: "Nominate a temple",
    titleLine1: "Know A Temple That",
    titleLine2: "Needs Our Help?",
    description:
      "Tell us about a temple in your village or town that is crumbling, closed or in need of repair. Our team will review it and, if it qualifies, launch a verified crowdfunding campaign to restore it.",
    stepsLabel: "What happens next",
    steps: [
      "Our team reviews your submission and contacts you",
      "Heritage experts visit the site and assess the damage",
      "A verified campaign goes live for the temple",
    ],
    formTitle: "Share Temple Details",
    formSubtitle: "It takes less than 2 minutes. Fields marked * are required.",
    submitLabel: "Submit Temple Details",
    privacyNote:
      "Your details are safe with us and will only be used to contact you about this temple.",
  },

  faq: {
    title: "Frequently Asked Questions",
    // Only the first answer is in the design; the rest are placeholder copy
    // until the admin-managed FAQ is wired up.
    items: [
      {
        question: "How is my donation used for temple restoration?",
        answer:
          "Your contribution goes into a dedicated fund for the temple you choose. Funds are released in stages to verified artisans and contractors as each milestone — foundation, walls, roof or carving — is completed and inspected. You receive photo updates at every stage and a final completion report.",
      },
      {
        question: "How are temples selected and verified?",
        answer:
          "Our team visits every nominated temple with heritage experts, checks its condition and local ownership, and prepares a costed restoration plan before a campaign goes live.",
      },
      {
        question: "Can I donate to a specific temple?",
        answer:
          "Yes. Choose any temple from the list above and click Donate Now — your contribution goes only to that temple's restoration fund.",
      },
      {
        question: "Is my donation tax-deductible?",
        answer:
          "Eligible donations receive an 80G receipt, which you can download from your profile after the payment is complete.",
      },
      {
        question: "Will I get updates on the restoration?",
        answer:
          "Yes. We share photo updates as each milestone is completed, and a final report when the temple reopens.",
      },
      {
        question: "Can I pause or cancel monthly giving?",
        answer:
          "Yes. You can pause or cancel your monthly giving at any time from the Subscriptions section of your profile.",
      },
    ],
  },
};
